import axios, { AxiosRequestConfig } from 'axios';
import { UserInfo } from '../dto/UserInfo';
import { mostraMensagemErro } from '../helpers/Notificacoes';

const REACT_APP_BASE_URL = process.env.REACT_APP_BASE_URL;

const api = axios.create({
    baseURL: REACT_APP_BASE_URL,
    withCredentials: true
});

// Rotas que nunca devem passar pelo interceptor de retry/refresh
const rotasDeAuth = ['/auth', '/auth/refresh', '/authAdm', '/auth/logout'];

const ehRotaDeAuth = (url?: string): boolean =>
    !url || rotasDeAuth.some(rota => url.includes(rota));

// Estado global do refresh
let isRefreshing = false;
let isLoggingOut = false;
// Uma vez true, bloqueia novas requisicoes ate o reload causado pelo redirect para o login
let sessaoEncerrada = false;
let filaDeFalhas: Array<{ resolve: () => void; reject: (err: unknown) => void }> = [];

// Garante que o navegador defina corretamente o boundary do multipart e barra novas chamadas apos sessao encerrada.
api.interceptors.request.use(
    config => {
        if (sessaoEncerrada && !ehRotaDeAuth(config.url)) {
            const erroCancelado = new Error('Sessão encerrada');
            erroCancelado.name = 'CanceledError';
            return Promise.reject(erroCancelado);
        }

        if (config.data instanceof FormData) {
            config.headers.delete(
                'Content-Type'
            );
        }

        return config;
    },
    error => Promise.reject(error)
);

const processarFila = (erro: unknown) => {
    filaDeFalhas.forEach(({ resolve, reject }) => {
        if (erro) reject(erro);
        else resolve();
    });
    filaDeFalhas = [];
};
//const userInfo: UserInfo = JSON.parse(localStorage.getItem('udbs')!);

const executarRefreshToken = async (
    body: Record<string, any> = {}
): Promise<boolean> => {

    const userInfo = JSON.parse(
        localStorage.getItem("udbs") || "null"
    ) as UserInfo | null;

    if (userInfo?.email === process.env.REACT_APP_ADM_MOBILE) {
        body = { email: userInfo?.email };
    }

    try {
        const resposta = await axios.post(
            `${REACT_APP_BASE_URL}/v1/auth/refresh`,
            body,
            {
                withCredentials: true,
                headers: {
                    "Content-Type": "application/json"
                }
            }
        );

        return resposta.status >= 200 && resposta.status < 300;
    } catch (erro) {
        console.error(erro);
        return false;
    }
};

const executarLogout = async () => {
    try {
        await axios.delete(
            `${REACT_APP_BASE_URL}/v1/auth/logout`,
            { withCredentials: true, headers: { 'Content-Type': 'application/json' } }
        );
    } catch (erro) {
        console.error("Erro ao executar logout apos falha de autenticacao:", erro);
    }
};

const redirecionarParaLogin = () => {
    window.location.replace('/');
};

type ConfigComRetry = AxiosRequestConfig & { _retry?: boolean };

api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const config = error.config as ConfigComRetry;

        if (ehRotaDeAuth(config?.url)) {
            return Promise.reject(error);
        }

        if (sessaoEncerrada) {
            return Promise.reject(error);
        }

        if (error.response?.status !== 401 || config._retry) {
            return Promise.reject(error);
        }

        // Marca como já tentado para não entrar em loop
        config._retry = true;

        if (isRefreshing) {
            // Já há um refresh em andamento — enfileira e aguarda
            return new Promise<void>((resolve, reject) => {
                filaDeFalhas.push({ resolve, reject });
            })
                .then(() => {
                    // Remove o signal original para evitar que um AbortSignal já expirado cancele o retry
                    const { signal: _, ...configSemSignal } = config;
                    return api.request(configSemSignal);
                })
                .catch(err => Promise.reject(err));
        }

        isRefreshing = true;

        try {
            console.log("[Auth] Iniciando renovacao de token...");
            const sucesso = await executarRefreshToken();
            //console.log("[Auth] Resultado da renovacao:", sucesso);

            if (sucesso) {
                // Libera a fila e reexecuta a requisição que falhou
                // Remove o signal original para evitar que um AbortSignal já expirado cancele o retry
                processarFila(null);
                const { signal: _, ...configSemSignal } = config;
                return api.request(configSemSignal);
            }

            // Refresh retornou false — sessão expirada definitivamente
            sessaoEncerrada = true;
            const erroSessao = new Error("Sessão expirada");
            processarFila(erroSessao);

            if (!isLoggingOut) {
                isLoggingOut = true;
                console.log("[Auth] Sessão expirada. Realizando logout...");
                await executarLogout();
                isLoggingOut = false;
            }

            mostraMensagemErro("Sessão expirada", undefined, redirecionarParaLogin);

            return Promise.reject(erroSessao);
        } catch (erroRefresh) {
            sessaoEncerrada = true;
            processarFila(erroRefresh);

            if (!isLoggingOut) {
                isLoggingOut = true;
                console.log("[Auth] Erro no refresh. Realizando logout...");
                await executarLogout();
                isLoggingOut = false;
            }

            mostraMensagemErro("Sessão expirada", undefined, redirecionarParaLogin);

            return Promise.reject(erroRefresh);
        } finally {
            isRefreshing = false;
        }
    }
);

export default api;